import { FC, useEffect, useState } from "react"
import { useSubmissions } from "../../stores/submissions"
import { RequestPaginator, SubmissionsService, TasksService } from "../../sdk"
import useError from "../../hooks/useError"
import Pagination from "../Pagination"
import PageLoader from "../PageLoader"
import classNames from "classnames"
import Table from "../Table"
import { submissionTestingClass, submissionTestingStatusMap } from "../../constants/submission"
import { formatDateHour } from "../../helpers/date"
import Tag from "../Tag"
import Code from "../Code"
import Modal from "../Modal"
import Message from "../Message"
import Button from "../Button"
import { useChallenge } from "../../stores/challenge"
import useDebounce from "../../hooks/useDebounce"
import Input from "../Input"
import Icon from "../Icon"
import { FiltersAndSortersType, PropertyAttributes } from "../../types/sorters-filters"
import { sortersMap, sortersValuesMap } from "../../constants/sorters"
import Select from "../Select"

const paginatorLimit = 20

const ChallengeSubmissions: FC = () => {
  const challenge = useChallenge(state => state.challenge)
  const tasks = useChallenge(state => state.tasks)
  const setTasks = useChallenge(state => state.setTasks)
  const { submissions, setSubmissions } = useSubmissions()
  const [currentOpenSubmission, setCurrentOpenSubmission] = useState<number | null>(null)
  const [currentOpenSubmissionCode, setCurrentOpenSubmissionCode] = useState<string | null>(null)

  const [count, setCount] = useState<number | null>(null)

  const [loading, setLoading] = useState<boolean>(!submissions)
  const [submissionCodeLoading, setSubmissionCodeLoading] = useState<boolean>(false)
  const [updatingTable, setUpdatingTable] = useState<boolean>(false)

  const [paginator, setPaginator] = useState<RequestPaginator>({ offset: 0, limit: paginatorLimit })
  const [pagination, setPagination] = useState<{ current: number, total: number } | null>({ current: 1, total: 1 })

  const [searchBarValue, setSearchBarValue] = useState<string>("")
  const debouncedValue = useDebounce(searchBarValue, 500)

  const [sorters, setSorters] = useState<FiltersAndSortersType["properties"] | null>(null)
  const [currentSorter, setCurrentSorter] = useState<{sorter: string | null, attributes: PropertyAttributes} | null>(null)
  const [currentSorterValue, setCurrentSorterValue] = useState<string | null>(null)

  const { handleError } = useError()

  const getTasks = async(): Promise<void> => {
    try {
      const { data } = await TasksService.listChallengeTasks({
        handler: {
          challengeId: challenge!.id
        },
        paginator: {
          offset: 0,
          limit: 100
        }
      })
      setTasks(data)
    } catch (err) {
      handleError(err)
    }
  }

  const getDetails = async(): Promise<void> => {
    try {
      const { count: submissionsCount, sorters } = await SubmissionsService.listSubmissions({
        paginator: { offset: 0, limit: 0 },
        getCount: true,
        getSorters: true,
        getFilters: true,
        filters: {
          challengeId: challenge!.id
        },
        search: debouncedValue === "" ? undefined : debouncedValue
      })
      setSorters((sorters as FiltersAndSortersType).properties)
      setCurrentSorter({ sorter: Object.entries((sorters as FiltersAndSortersType)?.properties)[0][0], attributes: Object.entries((sorters as FiltersAndSortersType)?.properties)[0][1] })
      setCount(submissionsCount || 0)
    } catch (err) {
      handleError(err)
    }
  }

  const getSubmissions = async(): Promise<void> => {
    if (!loading) {
      setUpdatingTable(true)
    }
    try {

      const { data } = await SubmissionsService.listSubmissions({
        paginator,
        filters: {
          challengeId: challenge!.id
        },
        search: debouncedValue === "" ? undefined : debouncedValue,
        sorters: currentSorter && { [currentSorter?.sorter!]: currentSorterValue || currentSorter.attributes.enum[0] } || {}
      })

      setSubmissions(data)
    } catch (err) {
      handleError(err)
    }
    setLoading(false)
    setUpdatingTable(false)
  }

  useEffect(() => {
    void getSubmissions()
  }, [paginator, debouncedValue, currentSorter, currentSorterValue])

  useEffect(() => {
    void (async(): Promise<void> => {
      if (!tasks) {
        await getTasks()
      }
      await getDetails()
    })()
  }, [debouncedValue])

  useEffect(() => {
    if (count && Math.ceil(count / paginatorLimit)) {
      setPagination({ current: 1, total: Math.ceil(count / paginatorLimit) })
    }
  }, [count])

  useEffect(() => {
    if (pagination && (pagination?.current - 1) * paginatorLimit !== paginator.offset) {
      setPaginator({ offset: (pagination?.current - 1) * paginatorLimit, limit: paginatorLimit })
    }
  }, [pagination])

  useEffect(() => {
    if (currentOpenSubmission) {
      void (async(): Promise<void> => {
        try {
          const { data: { code } } = await SubmissionsService.getSubmission({
            getCode: true,
            handler: { submissionId: currentOpenSubmission }
          })
          setCurrentOpenSubmissionCode(code)
        } catch (err) {
          handleError(err)
        }
        setSubmissionCodeLoading(false)
      })()
    }
  }, [currentOpenSubmission])

  return (
    <>
      {loading ?
        <PageLoader />
        :
        <>

          {submissions && submissions.length > 0
            ?
            <>
              <div className="flex items-center gap-2 mb-8">
                <h2 className="text-2xl font-semibold">Submissions</h2>

                <Button onClick={(): void => {
                  void getSubmissions()
                }}>Ricarica</Button>
              </div>

              <div className="users-list__sorters-wrapper">
                {sorters &&
                  <>
                    <Select
                      className="w-full md:w-[200px]"
                      label="Ordina per"
                      defaultValue={currentSorter && { label: sortersMap[currentSorter!.sorter!], value: currentSorter.attributes?.enum[0] }}
                      options={Object.keys(sorters).map(key => {
                        return {
                          label: sortersMap[key],
                          value: key
                        }
                      })}
                      onChange={(value): void => {
                        setCurrentSorter({ sorter: (value as { value: string }).value, attributes: sorters[(value as { value: string }).value] })
                      }}
                    />

                    <Select
                      className="w-full md:w-[200px]"
                      defaultValue={currentSorterValue && { label: sortersValuesMap[currentSorterValue], value: currentSorterValue }}
                      options={currentSorter?.attributes?.enum?.map(key => {
                        return {
                          label: sortersValuesMap[key],
                          value: key
                        }
                      })}
                      onChange={(value): void => {
                        setCurrentSorterValue((value as { value: string }).value)
                      }}
                    />
                  </>
                }
              </div>

              <div className="w-2/4 mt-4 mb-6">
                <Input
                  value={searchBarValue}
                  onChange={setSearchBarValue}
                  placeholder="Cerca..."
                  suffix={
                    searchBarValue !== "" &&
                    <span
                      onClick={(): void => {
                        setSearchBarValue("")
                      }}
                      className="hover:cursor-pointer hover:text-gray-500 w-8 h-8 flex justify-center items-center"
                    >
                      <Icon
                        name="x-close"
                        size={20}
                      />
                    </span>
                  }
                />
              </div>
              <Table
                className={classNames("users-list__table", updatingTable && "opacity-50 pointer-events-none")}
                headings={["ID", "Utente", "Task", "Stato Testing",  "Punteggio", "Data", "Codice", ""]}
                fixedColumns={["ID", ""]}
                rows={submissions.map(({
                  id,
                  testingStatus,
                  taskId,
                  score,
                  timestamp,
                  best,
                  submitter
                }) => {
                  return {
                    id: id.toString(),
                    children: [
                      <span>{id}</span>,
                      <span>{submitter.username}</span>,
                      <span>{tasks?.find(t => t.id === taskId)?.title || "-"}</span>,
                      <span className={submissionTestingClass[testingStatus]}>{submissionTestingStatusMap[testingStatus]}</span>,
                      <span>{score || 0}</span>,
                      <span>{formatDateHour(timestamp)}</span>,
                      <span
                        onClick={(): void => {
                          setCurrentOpenSubmission(id)
                          setSubmissionCodeLoading(true)
                        }}
                        className="text-indigo-700 underline hover:cursor-pointer"
                      >
                      Visualizza
                      </span>,
                      best && <Tag theme="green">BEST</Tag>
                    ]
                  }
                })}
              />

              {pagination && count !== null && count > paginatorLimit &&
                <Pagination
                  className={classNames("mt-4", updatingTable && "opacity-50 pointer-events-none")}
                  total={pagination.total}
                  current={pagination.current}
                  onChange={(page): void => {
                    if (page > 0 && page <= pagination.total && page !== pagination.current) {
                      setPagination({ current: page, total: pagination.total })
                    }
                  }}
                />
              }
            </>
            :
            <Message title="Nessuna submission presente" icon="file-off">
              <Button onClick={(): void => {
                void getSubmissions()
              }}>Ricarica</Button>
            </Message>
          }
          {currentOpenSubmission !== null &&
            <Modal
              className="submission-code-modal"
              close={(): void => setCurrentOpenSubmission(null)}
              title="Codice Submission"
            >
              {submissionCodeLoading ?
                <PageLoader className="relative my-8" />
                :
                currentOpenSubmissionCode !== null ?
                  <Code code={currentOpenSubmissionCode!} language="javascript" />
                  :
                  <p className="text-red-500 font-semibold">Errore</p>
              }
            </Modal>
          }
        </>
      }
    </>
  )
}

export default ChallengeSubmissions